import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import { fetchMealsThunk, deleteMealThunk, duplicateMealThunk } from "../../store/slices/mealsReducer";
import { fetchPatientProfilesThunk } from "../../store/slices/patientProfilesReducer";
import { fetchPatientMealInstancesThunk } from "../../store/slices/patientMealInstancesReducer";
import JourneyBreadcrumb from "../../components/navigation/JourneyBreadcrumb";
import { getDishAverages } from "../../utils/sustainabilityAverages";
import CircleChart from "../../components/CircleChart";
import { useAppAlertDialog } from "../../hooks/useAppAlertDialog";

const MealsListPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { list: meals, loading, error } = useSelector((state) => state.meals);
  const { profiles: patients } = useSelector((state) => state.patientProfiles);
  const patientMealInstances = useSelector((state) => state.patientMealInstances?.instances || []);
  
  // View state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list' | 'grouped'
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMealType, setSelectedMealType] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all' | 'assigned' | 'standalone'
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'oldest' | 'name_asc' | 'name_desc' | 'score_desc'
  
  // UI state
  const [deletingMealId, setDeletingMealId] = useState(null);
  const [duplicatingMealId, setDuplicatingMealId] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState(new Set());
  const [showAssignedPatientsModal, setShowAssignedPatientsModal] = useState(false);
  const [selectedMealForPatients, setSelectedMealForPatients] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const { showAlert, showConfirm, dialogNode } = useAppAlertDialog({
    cancelLabel: t("Avbryt"),
    confirmLabel: t("Fortsätt"),
    closeLabel: t("Stäng"),
  });
  
  // Meal type options
  const mealTypes = [
    { value: 'all', label: t('Alla') || 'Alla' },
    { value: 'frukost', label: t('Frukost') || 'Frukost' },
    { value: 'lunch', label: t('Lunch') || 'Lunch' },
    { value: 'middag', label: t('Middag') || 'Middag' },
    { value: 'mellanmål', label: t('Mellanmål') || 'Mellanmål' },
    { value: 'småmål', label: t('Småmål') || 'Småmål' },
    { value: 'kvällmål', label: t('Kvällsmål') || 'Kvällsmål' },
    { value: 'dessert', label: t('Desser') || 'Desser' },
    { value: 'övrigt', label: t('Övrigt') || 'Övrigt' },
  ];
  
  // Load meals, patients, and patient meal instances
  useEffect(() => {
    dispatch(fetchMealsThunk({}));
    // Fetch all patients (no filters) to ensure we have all patient names for the modal
    // Pass empty object to get all patients
    dispatch(fetchPatientProfilesThunk({}));
    // Fetch all patient meal instances to see which patients have which meals
    dispatch(fetchPatientMealInstancesThunk({}));
  }, [dispatch]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);
  
  // Filter and sort meals
  const filteredAndSortedMeals = useMemo(() => {
    let filtered = [...meals];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(meal => 
        meal.name?.toLowerCase().includes(query) ||
        meal.description?.toLowerCase().includes(query)
      );
    }
    
    // Meal type filter
    if (selectedMealType !== 'all') {
      filtered = filtered.filter(meal => meal.meal_type === selectedMealType);
    }
    
    // Patient filter
    if (selectedPatient !== 'all') {
      const patientId = parseInt(selectedPatient);
      filtered = filtered.filter(meal => meal.patient_profile_id === patientId);
    }
    
    // Assignment filter
    if (assignmentFilter === 'assigned') {
      filtered = filtered.filter(meal => meal.patient_profile_id !== null);
    } else if (assignmentFilter === 'standalone') {
      filtered = filtered.filter(meal => meal.patient_profile_id === null);
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'score_desc':
          const avgA = getDishAverages(a);
          const avgB = getDishAverages(b);
          const scoreA = (avgA.environment_average + avgA.health_average + avgA.social_average) / 3;
          const scoreB = (avgB.environment_average + avgB.health_average + avgB.social_average) / 3;
          return scoreB - scoreA;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [meals, searchQuery, selectedMealType, selectedPatient, assignmentFilter, sortBy]);
  
  // Group meals by type
  const groupedMeals = useMemo(() => {
    if (viewMode !== 'grouped') return null;
    
    const groups = {};
    filteredAndSortedMeals.forEach(meal => {
      const type = meal.meal_type || 'övrigt';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(meal);
    });
    
    return groups;
  }, [filteredAndSortedMeals, viewMode]);
  
  // Handle meal deletion
  const handleDeleteMeal = async (mealId, mealName) => {
    const confirmed = await showConfirm(
      t("Radera måltid"),
      t('Är du säker på att du vill radera måltiden "{{name}}"?', { name: mealName }) || `Är du säker på att du vill radera måltiden "${mealName}"?`
    );
    if (!confirmed) {
      return;
    }
    
    setDeletingMealId(mealId);
    try {
      await dispatch(deleteMealThunk(mealId)).unwrap();
      setSelectedMeals(prev => {
        const next = new Set(prev);
        next.delete(mealId);
        return next;
      });
    } catch (error) {
      showAlert(t("Fel"), t('Kunde inte radera måltiden') || 'Kunde inte radera måltiden');
    } finally {
      setDeletingMealId(null);
    }
  };
  
  // Handle meal duplication
  const handleDuplicateMeal = async (mealId) => {
    setDuplicatingMealId(mealId);
    try {
      const result = await dispatch(duplicateMealThunk({ mealId })).unwrap();
      if (result?.data?.id) {
        navigate(`/kostdata/meals/${result.data.id}/edit`);
      }
    } catch (error) {
      showAlert(t("Fel"), t('Kunde inte duplicera måltiden') || 'Kunde inte duplicera måltiden');
    } finally {
      setDuplicatingMealId(null);
    }
  };
  
  // Handle meal selection
  const toggleMealSelection = (mealId) => {
    setSelectedMeals(prev => {
      const next = new Set(prev);
      if (next.has(mealId)) {
        next.delete(mealId);
      } else {
        next.add(mealId);
      }
      return next;
    });
  };
  
  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedMeals.size === filteredAndSortedMeals.length) {
      setSelectedMeals(new Set());
    } else {
      setSelectedMeals(new Set(filteredAndSortedMeals.map(m => m.id)));
    }
  };
  
  // Get patient name
  const getPatientName = (patientId) => {
    if (!patientId) return null;
    // Try multiple matching strategies
    let patient = patients.find(p => p.id === patientId);
    if (!patient) {
      patient = patients.find(p => parseInt(p.id) === parseInt(patientId));
    }
    if (!patient) {
      patient = patients.find(p => String(p.id) === String(patientId));
    }
    return patient?.name || null;
  };
  
  // Get all assigned patients for a meal
  const getAssignedPatients = (meal) => {
    // Check both patient_profile_id on meals and patient_meal_instances
    const patientsViaProfileId = meals
      .filter(m => m.id === meal.id && m.patient_profile_id)
      .map(m => ({ meal: m, patientId: m.patient_profile_id, method: 'profile_id' }));
      
    const patientsViaInstances = (patientMealInstances || [])
      .filter(instance => instance && instance.meal_id === meal.id)
      .map(instance => ({ 
        meal: meal, 
        patientId: instance.patient_profile_id, 
        method: 'instance',
        instanceId: instance.id
      }));
    
    // Combine and deduplicate by patientId
    const allAssignments = [...patientsViaProfileId, ...patientsViaInstances];
    const uniquePatients = new Map();
    allAssignments.forEach(assignment => {
      if (!uniquePatients.has(assignment.patientId)) {
        uniquePatients.set(assignment.patientId, assignment);
      }
    });
    
    return Array.from(uniquePatients.values());
  };
  
  // Get meal averages
  const getMealAverages = (meal) => {
    return getDishAverages(meal);
  };
  
  // Meal card component
  const MealCard = ({ meal }) => {
    const averages = getMealAverages(meal);
    const isSelected = selectedMeals.has(meal.id);
    const isDeleting = deletingMealId === meal.id;
    const isDuplicating = duplicatingMealId === meal.id;
    const assignedPatients = getAssignedPatients(meal);
    const assignedCount = assignedPatients.length;
    
    const mealTypeLabels = {
      frukost: t('Frukost') || 'Frukost',
      lunch: t('Lunch') || 'Lunch',
      middag: t('Middag') || 'Middag',
      mellanmål: t('Mellanmål') || 'Mellanmål',
      småmål: t('Småmål') || 'Småmål',
      kvällmål: t('Kvällsmål') || 'Kvällsmål',
      dessert: t('Desser') || 'Desser',
      övrigt: t('Övrigt') || 'Övrigt',
    };
    
    return (
      <div
        className={`group relative bg-white rounded-xl shadow-sm border-2 transition-all duration-200 hover:shadow-lg hover:border-secondary-green ${
          isSelected ? 'border-secondary-green ring-2 ring-secondary-green ring-opacity-50' : 'border-gray-200'
        } ${isDeleting || isDuplicating ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {/* Selection checkbox */}
        <div className="absolute top-3 left-3 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleMealSelection(meal.id)}
            className="w-5 h-5 text-secondary-green border-gray-300 rounded focus:ring-secondary-green"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        
        <Link
          to={`/kostdata/meals/${meal.id}/edit`}
          className="block p-5"
        >
          {/* Image */}
          <div className="relative w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg mb-4 overflow-hidden">
            {meal.image ? (
              <img
                src={meal.image}
                alt={meal.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div className={`w-full h-full flex items-center justify-center ${meal.image ? 'hidden' : 'flex'}`}>
              <img
                src="/assets/icons/svg/default-image-placeholder.svg"
                alt=""
                className="w-16 h-16 text-gray-400"
              />
            </div>
            
            {/* Meal type badge */}
            <div className="absolute top-2 right-2">
              <span className="px-2 py-1 bg-white bg-opacity-90 backdrop-blur-sm rounded-md text-xs font-semibold text-gray-700">
                {mealTypeLabels[meal.meal_type] || meal.meal_type}
              </span>
            </div>
            
            {/* Patient badge - show count, hover to show modal */}
            {assignedCount > 0 && (
              <div 
                className="absolute bottom-2 left-2"
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  if (hoverTimeout) {
                    clearTimeout(hoverTimeout);
                    setHoverTimeout(null);
                  }
                  setSelectedMealForPatients({ 
                    meal, 
                    patientAssignments: assignedPatients
                  });
                  setShowAssignedPatientsModal(true);
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation();
                  // Delay closing to allow moving to modal
                  const timeout = setTimeout(() => {
                    setShowAssignedPatientsModal(false);
                  }, 200);
                  setHoverTimeout(timeout);
                }}
              >
                <span className="px-2 py-1 bg-blue-500 bg-opacity-90 backdrop-blur-sm rounded-md text-xs font-semibold text-white flex items-center gap-1 cursor-pointer hover:bg-opacity-100 transition-opacity">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  {assignedCount}
                </span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1">{meal.name}</h3>
            
            {meal.description && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{meal.description}</p>
            )}
            
            {/* Quick stats */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <span>{meal.components?.length || 0} {t('komponenter') || 'komponenter'}</span>
              {meal.size && meal.unit_abbr && (
                <span>{meal.size} {meal.unit_abbr}</span>
              )}
            </div>
            
            {/* Sustainability scores */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col justify-center items-center gap-1">
                <CircleChart value={averages.environment_average || 0} size={32} strokeWidth={4} label="" />
                <span className="text-xs text-gray-600">{t('Miljö') || 'Miljö'}</span>
              </div>
              <div className="flex flex-col justify-center items-center gap-1">
                <CircleChart value={averages.health_average || 0} size={32} strokeWidth={4} label="" />
                <span className="text-xs text-gray-600">{t('Hälsa') || 'Hälsa'}</span>
              </div>
              <div className="flex flex-col justify-center items-center gap-1">
                <CircleChart value={averages.social_average || 0} size={32} strokeWidth={4} label="" />
                <span className="text-xs text-gray-600">{t('Rättvisa') || 'Rättvisa'}</span>
              </div>
            </div>
          </div>
        </Link>
        
        {/* Actions */}
        <div className="px-5 pb-5 pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDuplicateMeal(meal.id);
              }}
              disabled={isDuplicating}
              className="p-2 text-gray-600 hover:text-secondary-green hover:bg-green-50 rounded-lg transition-colors"
              title={t('Duplicera') || 'Duplicera'}
            >
              {isDuplicating ? (
                <div className="w-5 h-5 border-2 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteMeal(meal.id, meal.name);
            }}
            disabled={isDeleting}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={t('Radera') || 'Radera'}
          >
            {isDeleting ? (
              <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  };
  
  // Meal list row component
  const MealListRow = ({ meal }) => {
    const averages = getMealAverages(meal);
    const isSelected = selectedMeals.has(meal.id);
    const patientName = getPatientName(meal.patient_profile_id);
    
    return (
      <tr className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-green-50' : ''}`}>
        <td className="px-4 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleMealSelection(meal.id)}
            className="w-4 h-4 text-secondary-green border-gray-300 rounded focus:ring-secondary-green"
          />
        </td>
        <td className="px-4 py-3">
          <Link to={`/kostdata/meals/${meal.id}/edit`} className="flex items-center gap-3 hover:text-secondary-green">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden flex-shrink-0">
              {meal.image ? (
                <img src={meal.image} alt={meal.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <img src="/assets/icons/svg/default-image-placeholder.svg" alt="" className="w-6 h-6" />
                </div>
              )}
            </div>
            <div>
              <div className="font-semibold text-gray-900">{meal.name}</div>
              {meal.description && (
                <div className="text-sm text-gray-500 line-clamp-1">{meal.description}</div>
              )}
            </div>
          </Link>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">{t(meal.meal_type) || meal.meal_type}</td>
        <td className="px-4 py-3 text-sm">
          {patientName ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs font-medium">
              {patientName}
            </span>
          ) : (
            <span className="text-gray-400 text-xs">{t('Standalone') || 'Standalone'}</span>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600">{meal.components?.length || 0}</td>
        <td className="px-4 py-3 text-sm text-gray-600">
          {meal.size && meal.unit_abbr ? `${meal.size} ${meal.unit_abbr}` : '-'}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <CircleChart value={averages.environment_average || 0} size={24} strokeWidth={3} label="" />
            <CircleChart value={averages.health_average || 0} size={24} strokeWidth={3} label="" />
            <CircleChart value={averages.social_average || 0} size={24} strokeWidth={3} label="" />
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateMeal(meal.id);
              }}
              className="p-1.5 text-gray-600 hover:text-secondary-green hover:bg-green-50 rounded transition-colors"
              title={t('Duplicera') || 'Duplicera'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteMeal(meal.id, meal.name);
              }}
              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title={t('Radera') || 'Radera'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  };
  
  if (loading && meals.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-600 text-lg font-medium">{t('Laddar måltider...') || 'Laddar måltider...'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t("Dashboard") || "Dashboard", to: "/" },
            { label: t("Måltider") || "Måltider" },
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t("Alla Måltider") || "Alla Måltider"}
              </h1>
              <p className="text-gray-600">
                {filteredAndSortedMeals.length} {t('måltider') || 'måltider'}
                {meals.length !== filteredAndSortedMeals.length && ` (${meals.length} ${t('totalt') || 'totalt'})`}
              </p>
            </div>
            
            <Link
              to="/kostdata/meals/new"
              className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-green text-white rounded-lg hover:opacity-90 transition-opacity font-semibold shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("Skapa Måltid") || "Skapa Måltid"}
            </Link>
          </div>
          
          {/* Search and Filters */}
          <div className="space-y-4">
            {/* Search bar */}
            <div className="relative">
              <input
                type="text"
                placeholder={t("Sök måltider...") || "Sök måltider..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-3.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Filters row */}
            <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-5 gap-3">
              {/* Meal type filter */}
              <select
                value={selectedMealType}
                onChange={(e) => setSelectedMealType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              >
                {mealTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              
              {/* Patient filter */}
              <select
                value={selectedPatient}
                onChange={(e) => setSelectedPatient(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              >
                <option value="all">{t('Alla patienter') || 'Alla patienter'}</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id.toString()}>{patient.name}</option>
                ))}
              </select>
              
              {/* Assignment filter */}
              <select
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              >
                <option value="all">{t('Alla') || 'Alla'}</option>
                <option value="assigned">{t('Tilldelade') || 'Tilldelade'}</option>
                <option value="standalone">{t('Standalone') || 'Standalone'}</option>
              </select>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-green focus:border-transparent"
              >
                <option value="newest">{t('Nyast först') || 'Nyast först'}</option>
                <option value="oldest">{t('Äldst först') || 'Äldst först'}</option>
                <option value="name_asc">{t('Namn A-Z') || 'Namn A-Z'}</option>
                <option value="name_desc">{t('Namn Z-A') || 'Namn Z-A'}</option>
                <option value="score_desc">{t('Högsta poäng') || 'Högsta poäng'}</option>
              </select>
              
              {/* View mode toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'grouped' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Bulk actions */}
            {selectedMeals.size > 0 && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-sm font-medium text-gray-700">
                  {selectedMeals.size} {t('valda') || 'valda'}
                </span>
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-secondary-green hover:underline"
                >
                  {selectedMeals.size === filteredAndSortedMeals.length ? t('Avmarkera alla') || 'Avmarkera alla' : t('Markera alla') || 'Markera alla'}
                </button>
                <div className="flex-1"></div>
                <button
                  onClick={async () => {
                    const confirmed = await showConfirm(
                      t("Radera valda måltider"),
                      t('Är du säker på att du vill radera {{count}} måltider?', { count: selectedMeals.size }) || `Är du säker på att du vill radera ${selectedMeals.size} måltider?`
                    );
                    if (!confirmed) return;
                    selectedMeals.forEach((mealId) => {
                      dispatch(deleteMealThunk(mealId));
                    });
                    setSelectedMeals(new Set());
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  {t('Radera valda') || 'Radera valda'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Meals Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        
        {filteredAndSortedMeals.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <img
              src="/assets/icons/svg/default-image-placeholder.svg"
              alt={t("Inga måltider")}
              className="w-24 h-24 mx-auto mb-4 text-gray-400"
            />
            <p className="text-gray-600 text-lg mb-4">
              {searchQuery || selectedMealType !== 'all' || selectedPatient !== 'all' || assignmentFilter !== 'all'
                ? t('Inga måltider matchar dina filter.') || 'Inga måltider matchar dina filter.'
                : t('Inga måltider ännu.') || 'Inga måltider ännu.'}
            </p>
            {(!searchQuery && selectedMealType === 'all' && selectedPatient === 'all' && assignmentFilter === 'all') && (
              <Link
                to="/kostdata/meals/new"
                className="inline-block px-6 py-3 bg-secondary-green text-white rounded-lg hover:opacity-90 transition-opacity font-semibold"
              >
                {t("Skapa första måltiden") || "Skapa första måltiden"}
              </Link>
            )}
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedMeals.size === filteredAndSortedMeals.length && filteredAndSortedMeals.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-secondary-green border-gray-300 rounded focus:ring-secondary-green"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Namn') || 'Namn'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Typ') || 'Typ'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Patient') || 'Patient'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Komponenter') || 'Komponenter'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Storlek') || 'Storlek'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Poäng') || 'Poäng'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{t('Åtgärder') || 'Åtgärder'}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedMeals.map(meal => (
                  <MealListRow key={meal.id} meal={meal} />
                ))}
              </tbody>
            </table>
          </div>
        ) : viewMode === 'grouped' ? (
          <div className="space-y-6">
            {Object.entries(groupedMeals || {}).map(([type, typeMeals]) => (
              <div key={type} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {mealTypes.find(t => t.value === type)?.label || type} ({typeMeals.length})
                </h2>
                <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-4">
                  {typeMeals.map(meal => (
                    <MealCard key={meal.id} meal={meal} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-4 desktop:grid-cols-5 gap-6">
            {filteredAndSortedMeals.map(meal => (
              <MealCard key={meal.id} meal={meal} />
            ))}
          </div>
        )}

      {/* Assigned Patients Modal - appears on hover */}
      {showAssignedPatientsModal && selectedMealForPatients && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none assigned-patients-modal"
          onMouseEnter={() => {
            if (hoverTimeout) {
              clearTimeout(hoverTimeout);
              setHoverTimeout(null);
            }
            setShowAssignedPatientsModal(true);
          }}
          onMouseLeave={() => {
            const timeout = setTimeout(() => {
              setShowAssignedPatientsModal(false);
            }, 200);
            setHoverTimeout(timeout);
          }}
        >
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden pointer-events-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('Tilldelade patienter') || 'Tilldelade patienter'}
                </h3>
                <button
                  onClick={() => setShowAssignedPatientsModal(false)}
                  onMouseEnter={(e) => e.stopPropagation()}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">{selectedMealForPatients.meal.name}</p>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-96 overflow-y-auto">
              {!selectedMealForPatients.patientAssignments || selectedMealForPatients.patientAssignments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t('Inga patienter har denna måltid tilldelad') || 'Inga patienter har denna måltid tilldelad'}</p>
              ) : (
                <div className="space-y-2">
                  {selectedMealForPatients.patientAssignments.map((assignment, index) => {
                    const patientId = assignment.patientId;
                    const patientName = patientId ? getPatientName(patientId) : null;
                    
                    // Debug logging to help identify issues
                    if (patientId && !patientName) {
                      console.log('[MealsListPage] Patient not found in modal:', {
                        patientId,
                        patientIdType: typeof patientId,
                        totalPatients: patients.length,
                        samplePatients: patients.slice(0, 5).map(p => ({ id: p.id, name: p.name, idType: typeof p.id })),
                        allPatientIds: patients.map(p => p.id)
                      });
                    }
                    
                    // Display patient name, or Patient ID, or Standalone
                    const displayText = patientName || (patientId ? `Patient ID: ${patientId}` : (t('Standalone') || 'Standalone'));
                    
                    return (
                      <div
                        key={assignment.instanceId || assignment.meal?.id || index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {displayText}
                            </p>
                            <p className="text-xs text-gray-500">
                              {assignment.method === 'instance' ? t('Via tilldelning') : t('Direkt tilldelad')}
                            </p>
                          </div>
                        </div>
                        {patientId && (
                          <Link
                            to={`/kostdata/patients/${patientId}`}
                            onClick={() => setShowAssignedPatientsModal(false)}
                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            {t('Visa') || 'Visa'}
                          </Link>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {dialogNode}
      </div>
    </div>
  );
};

export default MealsListPage;

